import React, { useState, useCallback } from 'react';
import { FeedPost, PostColorHex, POST_COLORS } from '../types';
import { MomentCard } from '../components/MomentCard';
import { EmptyState } from '../components/EmptyState';
import { ErrorState } from '../components/ErrorState';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import { useSession } from '../hooks/useSession';

interface Props {
  onNavigate: (page: string, params?: Record<string, string>) => void;
  onLoginRequired?: () => void;
}

export function ColorsPage({ onNavigate, onLoginRequired }: Props) {
  const session = useSession();
  const currentUserId = session.status === 'authenticated' ? session.user.id : null;
  const [selectedColor, setSelectedColor] = useState<PostColorHex | null>(null);
  const [posts, setPosts] = useState<FeedPost[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function selectColor(hex: PostColorHex) {
    if (loading) return;
    setSelectedColor(hex);
    setPosts([]);
    setNextCursor(null);
    setInitialized(false);
    setError(null);
    setLoading(true);
    try {
      const res = await fetch(`/api/v1/posts/color/${encodeURIComponent(hex)}`);
      if (!res.ok) throw new Error('Failed to load');
      const json = await res.json() as { data: FeedPost[]; next_cursor: string | null };
      setPosts(json.data);
      setNextCursor(json.next_cursor);
      setInitialized(true);
    } catch {
      setError('Could not load posts');
    } finally {
      setLoading(false);
    }
  }

  const loadMore = useCallback(async () => {
    if (!selectedColor || !nextCursor || loading) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/v1/posts/color/${encodeURIComponent(selectedColor)}?cursor=${encodeURIComponent(nextCursor)}`);
      if (!res.ok) throw new Error('Failed to load');
      const json = await res.json() as { data: FeedPost[]; next_cursor: string | null };
      setPosts(prev => [...prev, ...json.data]);
      setNextCursor(json.next_cursor);
    } catch {
      setError('Could not load more');
    } finally {
      setLoading(false);
    }
  }, [selectedColor, nextCursor, loading]);

  const sentinelRef = useInfiniteScroll(loadMore, !!nextCursor && !loading);

  const colorMeta = selectedColor ? POST_COLORS.find(c => c.hex === selectedColor) : null;

  return (
    <div className="fade-in">
      {/* Header */}
      <div style={stickyHeader}>
        <h2 style={{ fontSize: 18, fontWeight: 700 }}>Colors</h2>
        {selectedColor && (
          <button
            onClick={() => { setSelectedColor(null); setPosts([]); setInitialized(false); }}
            style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}
          >
            <ChevronLeft size={14} /> All colors
          </button>
        )}
      </div>

      {/* Color picker grid — shown when none selected */}
      {!selectedColor && (
        <div style={{ padding: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
            {POST_COLORS.map(c => (
              <button
                key={c.hex}
                onClick={() => selectColor(c.hex)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '14px 16px',
                  borderRadius: 'var(--radius)',
                  border: `1px solid ${c.hex}40`,
                  background: `${c.hex}12`,
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'background 0.15s, border-color 0.15s',
                }}
              >
                <div style={{ width: 20, height: 20, borderRadius: '50%', background: c.hex, flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{c.label}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>{c.vibe}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Posts for selected color */}
      {selectedColor && (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {/* Color header banner */}
          <div style={{
            padding: '12px 16px',
            borderBottom: '1px solid var(--border)',
            display: 'flex', alignItems: 'center', gap: 10,
            borderLeft: `3px solid ${selectedColor}`,
          }}>
            <div style={{ width: 12, height: 12, borderRadius: '50%', background: selectedColor, flexShrink: 0 }} />
            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{colorMeta?.label}</span>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{colorMeta?.vibe}</span>
            <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 'auto' }}>
              {initialized ? `${posts.length}${nextCursor ? '+' : ''} moment${posts.length !== 1 ? 's' : ''}` : '…'}
            </span>
          </div>

          {posts.map(post => (
            <div key={post.id} style={{ borderBottom: '1px solid var(--border)', padding: '12px 16px' }}>
              <MomentCard
                post={post}
                currentUserId={currentUserId}
                onLoginRequired={onLoginRequired}
                onResonate={() => {}}
                onReply={p => {
                  if (!currentUserId) { onLoginRequired?.(); return; }
                  onNavigate('post', { reply_to: p.id });
                }}
                onOpen={p => onNavigate('post-detail', { postId: p.id })}
                onNavigateUser={u => onNavigate('user', { username: u })}
                onDelete={id => setPosts(prev => prev.filter(p => p.id !== id))}
                onUpdate={updated => setPosts(prev => prev.map(p => p.id === updated.id ? updated : p))}
              />
            </div>
          ))}

          {loading && <Spinner color={selectedColor} />}

          {error && <ErrorState message={error} onRetry={() => selectColor(selectedColor)} />}

          {initialized && posts.length === 0 && !loading && !error && (
            <EmptyState
              heading="Nothing here yet"
              body={`Be the first to post a ${colorMeta?.label.toLowerCase()} moment.`}
            />
          )}

          <div ref={sentinelRef} style={{ height: 1 }} />
        </div>
      )}
    </div>
  );
}

function ChevronLeft({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function Spinner({ color }: { color: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', gap: 6, padding: 28 }}>
      {[0, 1, 2].map(i => (
        <div key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: color, animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite` }} />
      ))}
    </div>
  );
}

const stickyHeader: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '14px 16px',
  position: 'sticky', top: 0,
  background: 'rgba(10,10,10,0.92)',
  backdropFilter: 'blur(12px)',
  borderBottom: '1px solid var(--border)',
  zIndex: 10,
};
